import React, { FC, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { debounce } from 'lodash';
import { BarChart } from 'src/common/components/bar-chart';
import { Node } from 'src/common/interfaces/node.interface';
import { formatDuration } from 'src/utils/date';
import { getImageAssetSrcSet } from 'src/utils/use-image-asset-src-set.hook';
import { EditorInsightsPdfChartTooltip } from 'src/editor/insights/pdf-chart-tooltip';
import { DocumentInsight } from '../constants';
import classNames from 'classnames';

type Props = {
  node: Node;
  documentInsights: DocumentInsight[];
  chartOptions?: {
    yLabel?: string;
  };
};

function getDurationForPageIndex(documentInsights: DocumentInsight[], index: number): number {
  const insight = documentInsights.find((di) => di.page_index === index);
  if (!insight) {
    return 0;
  }
  const { num_sessions, total_time } = insight;
  return num_sessions && num_sessions > 0 ? Math.ceil(total_time / num_sessions) : 0;
}

export const DashboardInsightsJourneysJourneyPdfViewsChart: FC<Props> = ({ node, documentInsights, chartOptions }) => {
  const numOfPages = (node.file_asset?.data?.metadata?.page_sizes || []).length;
  const images = useMemo(
    () =>
      (node.image_collection?.images || []).map((image) => ({
        src: image?.image_asset?.url,
        srcSet: getImageAssetSrcSet(image?.image_asset, 'thumbnail_300x300'),
      })),
    [node]
  );

  const renderTooltip = useCallback((context: any, images: any, tooltipElRef: React.RefObject<HTMLElement>) => {
    if (tooltipElRef.current) {
      const { chart, tooltip } = context;
      const datapoint = tooltip.dataPoints[0];
      const dataX = datapoint?.parsed.x;
      const dataY = datapoint?.parsed.y;

      const dataIndex = datapoint?.dataIndex;
      const totalPages = datapoint.dataset.data.length;

      const image = images[dataIndex];

      ReactDOM.render(
        <EditorInsightsPdfChartTooltip
          key={`${dataX + 1}`}
          x={chart.canvas.offsetLeft + tooltip.caretX}
          y={chart.canvas.offsetTop + tooltip.caretY}
          show={tooltip.opacity !== 0}
          page={dataX + 1}
          totalPages={totalPages}
          time={dataY}
          imageSrc={image.src}
          imageSrcSet={image.srcSet}
        />,
        tooltipElRef.current
      );
    }
  }, []);

  const debouncedRenderTooltip = useMemo(
    () =>
      debounce(
        (context: any, images: any, tooltipElRef: React.RefObject<HTMLElement>) =>
          renderTooltip(context, images, tooltipElRef),
        50
      ),
    [renderTooltip]
  );

  if (node.content_type !== 'pdf') {
    return null;
  }

  if (documentInsights.length === 0) {
    return null;
  }

  const fitToContainer = numOfPages <= 30;

  return (
    <div className={`relative w-full h-[300px]`}>
      <div
        className={classNames('absolute h-full', {
          'w-full': fitToContainer,
          'w-[960px] overflow-x-auto': !fitToContainer,
        })}
      >
        <BarChart
          canvasClasses={classNames('h-full w-full')}
          canvasStyle={{
            ...(!fitToContainer && {
              width: `${numOfPages * 24}px`,
            }),
          }}
          data={[...Array(numOfPages)].map((x, index) => getDurationForPageIndex(documentInsights, index))}
          labels={[...Array(numOfPages)].map((x, i) => `${i + 1}`)}
          labelXAxis='Pages'
          labelYAxis={chartOptions?.yLabel || 'Time in seconds'}
          tickLabelFormatterY={(value) => formatDuration(value)}
          renderTooltip={(context, tooltipElRef) => debouncedRenderTooltip(context, images, tooltipElRef)}
        />
      </div>
    </div>
  );
};
